import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useRemovePlmEventMutation,
  usePlmEventsQuery,
  usePlmEventEventsSubscription,
} from '../../../../types/generated-types';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add,
} from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import {
  useInjectableComponents,
  BottomMenuItem,
} from '../../../system/services/injectableComponentsManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';

type PlmEventItem = NonNullable<
  NonNullable<
    NonNullable<
      ReturnType<typeof usePlmEventsQuery>['data']
    >['paginatedPlmEvents']
  >['items']
>[0];

export const PlmEventList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuthorizer();
  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();
  const [plmEvents, setPlmEvents] = useState<PlmEventItem[]>([]);

  const { data, loading, error } = usePlmEventsQuery({
    variables: {
      page: 1,
      perPage: 1000,
    },
    fetchPolicy: 'network-only',
  });

  const [deletePlmEvent] = useRemovePlmEventMutation();

  // Update local state when data changes
  useEffect(() => {
    if (data?.paginatedPlmEvents?.items) {
      setPlmEvents(data.paginatedPlmEvents.items);
    }
  }, [data]);

  // Subscribe to real-time updates
  usePlmEventEventsSubscription({
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (error) {
      Notifier.error('There was a problem fetching the PLM events: ', error);
    }
  }, [error]);

  const handleRemovePlmEvent = useCallback(
    async (id: string) => {
      if (!can('delete', 'PlmEvent')) {
        Notifier.error('You do not have permission to delete PLM events');
        return;
      }

      try {
        await deletePlmEvent({
          variables: { id },
        });
        Notifier.success('PLM event deleted successfully');
      } catch (error) {
        Notifier.error('Failed to delete PLM event');
      }
    },
    [deletePlmEvent, can],
  );

  const handleViewPlmEvent = useCallback(
    (id: string) => {
      navigate(`/plm-events/${id}`);
    },
    [navigate],
  );

  const handleEditPlmEvent = useCallback(
    (id: string) => {
      navigate(`/plm-events/${id}/edit`);
    },
    [navigate],
  );

  const handleAddPlmEvent = useCallback(() => {
    navigate('/plm-events/add');
  }, [navigate]);

  const isSmall = useIsSmallScreen();

  // Set title and breadcrumb
  useEffect(() => {
    setTitle(isSmall ? 'PLM Events' : 'Peak Load Management Events');
    setTitlePath('/plm-events');
  }, [setTitle, setTitlePath, isSmall]);

  // Set context menu items
  useEffect(() => {
    const menuItems: BottomMenuItem[] = [];

    if (can('create', 'PlmEvent')) {
      menuItems.push({
        id: 'add-plm-event',
        label: isSmall ? 'Add PLM Event' : 'Add Peak Load Management Event',
        icon: <Add />,
        action: handleAddPlmEvent,
      });
    }

    setContextMenuItems([{ items: menuItems }]);
  }, [can, handleAddPlmEvent, setContextMenuItems, isSmall]);

  const columns: MRT_ColumnDef<PlmEventItem>[] = [
    {
      accessorKey: 'program.name',
      header: 'Program',
    },
    {
      accessorKey: 'contract.name',
      header: 'Contract',
    },
    {
      accessorKey: 'startTime',
      header: 'Start Time',
      Cell: ({ cell }) => {
        const startTime = cell.getValue<number>();
        return startTime ? new Date(startTime).toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'duration',
      header: 'Duration (min)',
    },
    {
      accessorKey: 'reductionTarget',
      header: 'Reduction Target',
      Cell: ({ cell }) => {
        const target = cell.getValue<number>();
        return target ? `${target} kW` : '-';
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => {
        const status = cell.getValue<string>();
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : '-';
      },
    },
    {
      accessorKey: 'notificationMethod',
      header: 'Notification Method',
      Cell: ({ cell }) => {
        const method = cell.getValue<string>();
        return method ? method.toUpperCase() : '-';
      },
    },
    {
      accessorKey: 'notificationReceivedAt',
      header: 'Notification Received',
      Cell: ({ cell }) => {
        const receivedAt = cell.getValue<number>();
        return receivedAt ? new Date(receivedAt).toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'notificationPayload',
      header: 'Notification Body',
      Cell: ({ cell }) => {
        const payload = cell.getValue<string>();
        return payload
          ? payload.length > 50
            ? `${payload.substring(0, 50)}...`
            : payload
          : '-';
      },
    },
  ];

  const actionsColumn: MRT_ColumnDef<PlmEventItem> = {
    id: 'actions',
    header: 'Actions',
    size: 120,
    Cell: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="View">
          <IconButton
            size="small"
            onClick={() => handleViewPlmEvent(row.original._id)}
          >
            <ViewIcon />
          </IconButton>
        </Tooltip>
        {can('update', 'PlmEvent') && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditPlmEvent(row.original._id)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
        {can('delete', 'PlmEvent') && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleRemovePlmEvent(row.original._id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
  };

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <MaterialReactTable
        columns={[...columns, actionsColumn]}
        data={plmEvents}
        defaultColumn={{ size: 150 }}
        state={{
          isLoading: loading,
          columnVisibility: {
            duration: false,
            reductionTarget: false,
            notificationMethod: false,
            notificationReceivedAt: false,
            notificationPayload: false,
          },
        }}
        enableColumnFilters
        enableFullScreenToggle={false}
        enableDensityToggle={false}
        enableSorting
        enablePagination
        enableRowSelection={false}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 'bold',
          },
        }}
        layoutMode="semantic"
      />
    </Box>
  );
};
